import axios from 'axios';

import {
  API_BASE_URL,
  MAX_RETRY_COUNT,
  HTTP_STATUS,
} from '../constants';

import { logError } from '../common/utils/logger';

/**
 * =========================
 * REQUEST INTERCEPTOR
 * =========================
 */
const attachAuthHeaders = async (config) => {
  config.headers = config.headers || {};
  const method = String(config.method || 'get').toLowerCase();
  const isStateChanging = ['post', 'put', 'patch', 'delete'].includes(method);

  // Attach Liferay CSRF token (required for BFF)
  if (window.Liferay?.authToken) {
    config.headers['X-CSRF-Token'] = window.Liferay.authToken;
  } else if (isStateChanging) {
    return Promise.reject(
      new Error(`Missing CSRF token for ${method.toUpperCase()} request.`),
    );
  }

  // Handle FormData properly
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
    delete config.headers['content-type'];
    config.transformRequest = [];
  }

  return config;
};

/**
 * =========================
 * AXIOS INSTANCE
 * =========================
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true // VERY IMPORTANT for BFF (cookies/session)
});

api.interceptors.request.use(attachAuthHeaders, Promise.reject);

/**
 * =========================
 * RESPONSE ERROR HANDLER
 * =========================
 */
const handleResponseError = async (error) => {
  const { response, config } = error;

  if (!response || !config) {
    return Promise.reject(error);
  }

  config._retryCount = config._retryCount || 0;

  // Retry only for auth issues (handled by BFF/session)
  if (
    (response.status === HTTP_STATUS.UNAUTHORIZED ||
      response.status === HTTP_STATUS.FORBIDDEN) &&
    config._retryCount < MAX_RETRY_COUNT
  ) {
    config._retryCount += 1;

    // No token refresh here — BFF handles auth
    return api(config);
  }

  logError('[API] Request failed', {
    url: config.url,
    status: response.status,
    data: response.data
  });

  return Promise.reject(error);
};

api.interceptors.response.use(
  (res) => res,
  handleResponseError
);

export default api;